import { loadLocalTeam, type LocalTeam } from './localTeam';

/**
 * Demo data seeder. Stuffs realistic-looking players + ratings + clips
 * into localStorage so the radar / velocity / welfare flags actually
 * have something to compute against in the no-DB demo mode.
 *
 * Schema-compatible field names so when a real Supabase comes online,
 * the same shapes are read by the same hooks.
 */

const PLAYERS_KEY  = '4cm_local_players';
const RATINGS_KEY  = '4cm_local_ratings';
const CLIPS_KEY    = '4cm_local_video_tags';
const GOALS_KEY    = '4cm_local_player_goals';
const PLANS_KEY    = '4cm_local_dev_plans';
const CHECKIN_KEY  = '4cm_local_growth_checkins';

export interface LocalPlayer {
  id: string;
  team_id: string;
  name: string;
  position: string | null;
}

export interface LocalRating {
  id: string;
  ratee_id: string;
  rater_id: string;
  corner_id: string;
  score: number;
  created_at: string;
  revealed_at: string;
  state: 'REVEALED';
}

export interface LocalVideoTag {
  id: string;
  player_id: string;
  clip_url: string;
  platform: 'YOUTUBE' | 'VEO' | 'HUDL' | 'INTERNAL';
  notes: string | null;
  ts_in: number;
  ts_out: number;
  corner_id: string | null;
  created_at: string;
}

/**
 * Player-goal memory persisted locally when Supabase is the placeholder env.
 * Mirrors the brain_memories row shape we'd write upstream so the read
 * path can present the same `fact` text either way.
 */
export interface LocalPlayerGoal {
  id: string;
  coach_id: string;
  player_id: string;
  player_name: string | null;
  fact: string;
  tags: string[];
  created_at: string;
}

/**
 * Coach growth check-in persisted locally when Supabase is the placeholder
 * env. Mirrors the brain_memories shape so the offline Brain prior gather
 * can mix it with live memories without caring about source.
 */
export interface LocalGrowthCheckin {
  id: string;
  coach_id: string;
  team_id: string | null;
  fact: string;
  /** Structured per-question answer for downstream prior-weaving. */
  answers: Array<{ questionId: string; prompt: string; answer: string }>;
  tags: string[];
  created_at: string;
}

/**
 * 4-week development plan persisted locally when Supabase is the placeholder
 * env. Schema mirrors the `development_plans` table fields the live save
 * path writes — read path in `loadLocalDevPlan` returns the most recent
 * active plan for a player.
 */
export interface LocalDevPlan {
  id: string;
  coach_id: string;
  player_id: string;
  period_start: string;
  period_end: string;
  primary_corner: string;
  milestones: Array<{ text: string; due_date: string; met_at?: string | null }>;
  review_date: string;
  created_at: string;
}

const FIRST_NAMES = ['Marco', 'Sarah', 'Kai', 'Aiden', 'Maya', 'Theo', 'Ava', 'Leo', 'Iris', 'Jonah', 'Mira', 'Asher'];
const POSITIONS  = ['GK', 'CB', 'CB', 'LB', 'RB', 'CM', 'CM', 'CM', 'LW', 'RW', 'ST', 'ST'];

const CORNERS = ['corner-technical', 'corner-physical', 'corner-psych', 'corner-social'] as const;

interface SeedOptions {
  coachId: string;
}

export function seedDemoData(opts: SeedOptions): { team: LocalTeam | null; players: number; ratings: number; clips: number } | null {
  const team = loadLocalTeam();
  if (!team) return null; // need a team first (run /onboarding)

  const players: LocalPlayer[] = FIRST_NAMES.map((name, i) => ({
    id: `local-player-${team.id}-${i}`,
    team_id: team.id,
    name: `${name} ${String.fromCharCode(65 + i)}.`,
    position: POSITIONS[i] ?? 'CM',
  }));

  const ratings: LocalRating[] = [];
  for (const p of players) {
    // Each player gets 3-5 ratings across all four corners over the last
    // 28 days. Most stay flat; a few get a velocity drop in the last
    // fortnight to trigger welfare flags.
    const isDrop = p.id.endsWith('-2'); // Kai gets a drop
    const isLowPS = p.id.endsWith('-7'); // Leo psych-social low

    CORNERS.forEach((corner, ci) => {
      const baseline = 60 + ci * 5 + (p.id.charCodeAt(p.id.length - 1) % 11);
      // 5 prior + 5 recent
      for (let i = 0; i < 5; i++) {
        const days = 28 - i * 3;
        const noise = (Math.random() - 0.5) * 8;
        ratings.push(rating(p.id, opts.coachId, corner, Math.round(baseline + noise), days));
      }
      for (let i = 0; i < 5; i++) {
        const days = 12 - i * 2;
        let score = baseline + (Math.random() - 0.5) * 8;
        if (isDrop && corner === 'corner-technical') score -= 22;
        if (isLowPS && (corner === 'corner-psych' || corner === 'corner-social')) score -= 30;
        ratings.push(rating(p.id, opts.coachId, corner, Math.round(score), days));
      }
    });
  }

  const clips: LocalVideoTag[] = players.slice(0, 4).flatMap((p, i) => [
    {
      id: `local-clip-${p.id}-1`,
      player_id: p.id,
      clip_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      platform: 'YOUTUBE',
      notes: `${p.name.split(' ')[0]} found the third-man run twice in this rep — keep watching the trigger`,
      ts_in: 12 + i * 4,
      ts_out: 30 + i * 4,
      corner_id: i % 2 === 0 ? 'corner-technical' : 'corner-social',
      created_at: new Date(Date.now() - (3 + i) * 86_400_000).toISOString(),
    },
  ]);

  try {
    localStorage.setItem(PLAYERS_KEY, JSON.stringify(players));
    localStorage.setItem(RATINGS_KEY, JSON.stringify(ratings));
    localStorage.setItem(CLIPS_KEY, JSON.stringify(clips));
  } catch (err) {
    console.warn('[demo] could not persist', err);
    return null;
  }

  return { team, players: players.length, ratings: ratings.length, clips: clips.length };
}

function rating(playerId: string, coachId: string, cornerId: string, score: number, daysAgo: number): LocalRating {
  const created = new Date(Date.now() - daysAgo * 86_400_000).toISOString();
  return {
    id: `local-rating-${playerId}-${cornerId}-${daysAgo}`,
    ratee_id: playerId,
    rater_id: coachId,
    corner_id: cornerId,
    score: Math.max(0, Math.min(100, score)),
    created_at: created,
    revealed_at: created,
    state: 'REVEALED',
  };
}

export function loadLocalPlayers(): LocalPlayer[] {
  try {
    const raw = localStorage.getItem(PLAYERS_KEY);
    return raw ? (JSON.parse(raw) as LocalPlayer[]) : [];
  } catch { return []; }
}

/**
 * Append a coach-added player to the local roster. Reuses the same key
 * the demo seed writes to, so they sit alongside seeded players in
 * useTeamPlayers' fallback path. Coach-added players carry an
 * `added_at` so we could later filter or distinguish them in the UI.
 */
export function appendLocalPlayer(opts: {
  team_id: string;
  name: string;
  position: string | null;
}): LocalPlayer {
  const row: LocalPlayer = {
    id: `local-coach-player-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    team_id: opts.team_id,
    name: opts.name.trim(),
    position: opts.position?.trim() || null,
  };
  try {
    const existing = loadLocalPlayers();
    localStorage.setItem(PLAYERS_KEY, JSON.stringify([...existing, row]));
  } catch (err) {
    console.warn('[local players] could not persist', err);
    throw new Error('Could not save player locally — storage unavailable.');
  }
  return row;
}

export function removeLocalPlayer(playerId: string): void {
  try {
    const existing = loadLocalPlayers();
    localStorage.setItem(PLAYERS_KEY, JSON.stringify(existing.filter((p) => p.id !== playerId)));
  } catch (err) {
    console.warn('[local players] could not remove', err);
  }
}

export function loadLocalRatings(): LocalRating[] {
  try {
    const raw = localStorage.getItem(RATINGS_KEY);
    return raw ? (JSON.parse(raw) as LocalRating[]) : [];
  } catch { return []; }
}

export function loadLocalVideoTags(): LocalVideoTag[] {
  try {
    const raw = localStorage.getItem(CLIPS_KEY);
    return raw ? (JSON.parse(raw) as LocalVideoTag[]) : [];
  } catch { return []; }
}

/** Append a coach-created clip tag alongside any seeded demo clips. */
export function appendLocalVideoTag(tag: Omit<LocalVideoTag, 'id' | 'created_at'>): LocalVideoTag {
  const row: LocalVideoTag = {
    ...tag,
    id: `local-clip-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    created_at: new Date().toISOString(),
  };
  try {
    const existing = loadLocalVideoTags();
    localStorage.setItem(CLIPS_KEY, JSON.stringify([...existing, row]));
  } catch (err) {
    console.warn('[local clips] could not persist', err);
    throw new Error('Could not save clip tag locally — storage unavailable.');
  }
  return row;
}

export function loadLocalPlayerGoals(coachId: string, playerId: string): LocalPlayerGoal[] {
  try {
    const raw = localStorage.getItem(GOALS_KEY);
    const all = raw ? (JSON.parse(raw) as LocalPlayerGoal[]) : [];
    return all
      .filter((g) => g.coach_id === coachId && g.player_id === playerId)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  } catch { return []; }
}

export function appendLocalPlayerGoal(
  goal: Omit<LocalPlayerGoal, 'id' | 'created_at'>,
): LocalPlayerGoal {
  const row: LocalPlayerGoal = {
    ...goal,
    id: `local-goal-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    created_at: new Date().toISOString(),
  };
  try {
    const raw = localStorage.getItem(GOALS_KEY);
    const all = raw ? (JSON.parse(raw) as LocalPlayerGoal[]) : [];
    localStorage.setItem(GOALS_KEY, JSON.stringify([...all, row]));
  } catch (err) {
    console.warn('[local goals] could not persist', err);
    throw new Error('Could not save goal locally — storage unavailable.');
  }
  return row;
}

export function loadLocalGrowthCheckins(coachId: string, limit = 10): LocalGrowthCheckin[] {
  try {
    const raw = localStorage.getItem(CHECKIN_KEY);
    const all = raw ? (JSON.parse(raw) as LocalGrowthCheckin[]) : [];
    return all
      .filter((c) => c.coach_id === coachId)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, limit);
  } catch { return []; }
}

export function appendLocalGrowthCheckin(
  checkin: Omit<LocalGrowthCheckin, 'id' | 'created_at'>,
): LocalGrowthCheckin {
  const row: LocalGrowthCheckin = {
    ...checkin,
    id: `local-checkin-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    created_at: new Date().toISOString(),
  };
  try {
    const raw = localStorage.getItem(CHECKIN_KEY);
    const all = raw ? (JSON.parse(raw) as LocalGrowthCheckin[]) : [];
    localStorage.setItem(CHECKIN_KEY, JSON.stringify([...all, row]));
  } catch (err) {
    console.warn('[local checkins] could not persist', err);
    throw new Error('Could not save check-in locally — storage unavailable.');
  }
  return row;
}

export function loadLocalDevPlans(coachId: string, playerId: string): LocalDevPlan[] {
  try {
    const raw = localStorage.getItem(PLANS_KEY);
    const all = raw ? (JSON.parse(raw) as LocalDevPlan[]) : [];
    return all
      .filter((p) => p.coach_id === coachId && p.player_id === playerId)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  } catch { return []; }
}

export function appendLocalDevPlan(
  plan: Omit<LocalDevPlan, 'id' | 'created_at'>,
): LocalDevPlan {
  const row: LocalDevPlan = {
    ...plan,
    id: `local-plan-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    created_at: new Date().toISOString(),
  };
  try {
    const raw = localStorage.getItem(PLANS_KEY);
    const all = raw ? (JSON.parse(raw) as LocalDevPlan[]) : [];
    localStorage.setItem(PLANS_KEY, JSON.stringify([...all, row]));
  } catch (err) {
    console.warn('[local plans] could not persist', err);
    throw new Error('Could not save plan locally — storage unavailable.');
  }
  return row;
}

export function clearDemoData(): void {
  try {
    localStorage.removeItem(PLAYERS_KEY);
    localStorage.removeItem(RATINGS_KEY);
    localStorage.removeItem(CLIPS_KEY);
    localStorage.removeItem(GOALS_KEY);
    localStorage.removeItem(PLANS_KEY);
    localStorage.removeItem(CHECKIN_KEY);
  } catch { /* noop */ }
}

export function hasDemoData(): boolean {
  return loadLocalPlayers().length > 0;
}
